// Recarga las pestañas que siguen con una versión vieja.
//
// ANDI y WhatsApp se quedan abiertos días. El 17/09 el arreglo del espejo tras
// suspender el equipo no llegaba a nadie que no recargara, y un tercio de las
// pestañas seguía leyendo de Firestore con el código de la víspera. index.html
// se sirve sin caché y nombra el script principal con su hash: si el publicado
// no es el de esta pestaña, hay versión nueva. Se recarga solo cuando nadie
// está mirando (o la pestaña lleva mucho rato sin tocarse) y no hay nada a
// medias: ni un diálogo abierto, ni un campo con el foco, ni un texto sin
// enviar, ni escrituras pendientes en Firestore.

const COMPROBAR_CADA_MS = 5 * 60e3;
// A la vista, solo si la versión nueva lleva una hora esperando y nadie ha
// tocado la pestaña en 15 min.
const PENDIENTE_A_LA_VISTA_MS = 60 * 60e3;
const SIN_TOCAR_MS = 15 * 60e3;
const ESPERA_ESCRITURAS_MS = 5000;
const CLAVE_RECARGA = "nuevaVersion:recargadaPara";

const SCRIPT_PRINCIPAL = /\/static\/js\/main\.[0-9a-f]{8,}\.js/;

export function scriptPrincipal(texto: string): string | null {
  const m = SCRIPT_PRINCIPAL.exec(texto);
  return m ? m[0] : null;
}

function scriptDeEstaPagina(): string | null {
  for (const s of Array.from(document.scripts)) {
    const propio = scriptPrincipal(s.src || "");
    if (propio) return propio;
  }
  return null;
}

// El hash de esta pestaña y el del último index.html que ha visto. Van al VPS
// con cada petición, y así los administradores saben quién sigue con una
// versión vieja (18/09).
const HASH = /main\.([0-9a-f]{8,})\.js/;
const hashDe = (script: string | null) => (script && HASH.exec(script)?.[1]) || null;
let publicadaVista: string | null = null;

export function versionPropia(): string | null {
  return hashDe(scriptDeEstaPagina());
}

export function versionPublicada(): string | null {
  return publicadaVista;
}

// Nada a medias que una recarga pueda perder.
export function hayAlgoAMedias(): boolean {
  if (document.querySelector('[role="dialog"], [aria-modal="true"], dialog[open], .fixed.inset-0')) return true;
  const activo = document.activeElement as HTMLElement | null;
  if (activo && activo !== document.body &&
      (/^(INPUT|TEXTAREA|SELECT)$/.test(activo.tagName) || activo.isContentEditable)) return true;
  return Array.from(document.querySelectorAll("textarea")).some((t) => t.value.trim() !== "");
}

type Opciones = {
  // Resuelve cuando Firestore ha confirmado todas las escrituras de la pestaña.
  esperarEscrituras: () => Promise<void>;
  recargar?: () => void;
  pedirIndice?: () => Promise<string>;
};

export function vigilarVersion({ esperarEscrituras, recargar, pedirIndice }: Opciones): () => void {
  const propio = scriptDeEstaPagina();
  // En desarrollo no hay hash: nada que comparar.
  if (!propio) return () => {};
  const recargarPagina = recargar || (() => window.location.reload());
  const indice = pedirIndice || (() => fetch(`/?v=${Date.now()}`, { cache: "no-store" }).then((r) => r.text()));
  let publicado: string | null = null;
  let desde = 0;
  let ultimoToque = Date.now();
  let ocupado = false;
  let parado = false;

  const tocar = () => { ultimoToque = Date.now(); };

  const quizaRecargar = async () => {
    if (parado || ocupado || !publicado) return;
    const aLaVista = document.visibilityState !== "hidden";
    if (aLaVista && (Date.now() - desde < PENDIENTE_A_LA_VISTA_MS || Date.now() - ultimoToque < SIN_TOCAR_MS)) return;
    if (hayAlgoAMedias()) return;
    ocupado = true;
    try {
      const listo = await Promise.race([
        esperarEscrituras().then(() => true, () => false),
        new Promise<boolean>((r) => setTimeout(() => r(false), ESPERA_ESCRITURAS_MS)),
      ]);
      if (!listo || parado || hayAlgoAMedias()) return;
      // Si ya se recargó por esta versión y sigue llegando la vieja (caché de
      // la CDN), no se insiste: se evita un bucle de recargas.
      try {
        if (sessionStorage.getItem(CLAVE_RECARGA) === publicado) return;
        sessionStorage.setItem(CLAVE_RECARGA, publicado);
      } catch { /* sin sessionStorage se recarga igual, una vez por pestaña */ }
      parado = true;
      recargarPagina();
    } finally {
      ocupado = false;
    }
  };

  const comprobar = async () => {
    if (parado) return;
    try {
      const actual = scriptPrincipal(await indice());
      if (actual) publicadaVista = hashDe(actual);
      if (actual && actual !== propio) {
        if (publicado !== actual) { publicado = actual; desde = Date.now(); }
      }
    } catch { /* sin red: la próxima vez */ }
    await quizaRecargar();
  };

  const alCambiarVisibilidad = () => {
    if (document.visibilityState === "hidden") void quizaRecargar();
    else void comprobar();
  };

  const temporizador = setInterval(() => { void comprobar(); }, COMPROBAR_CADA_MS);
  document.addEventListener("visibilitychange", alCambiarVisibilidad);
  window.addEventListener("pointerdown", tocar, true);
  window.addEventListener("keydown", tocar, true);
  void comprobar();
  return () => {
    parado = true;
    clearInterval(temporizador);
    document.removeEventListener("visibilitychange", alCambiarVisibilidad);
    window.removeEventListener("pointerdown", tocar, true);
    window.removeEventListener("keydown", tocar, true);
  };
}
